import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Checkbox, FormControlLabel, useTheme } from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { LabeledTextField } from "../../components";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { TextFieldCounter } from "../../components/TextFieldWithCounter";
import { USER_GROUPS_QUERY_KEY } from "../../hooks/useAllUserGroupsForACompany";
import { COMPANY_DEFAULT_USER_GROUPS_QUERY_KEY } from "../../hooks/useCompanyDefaultUserGroups";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useLocalization } from "../../hooks/useLocalization";
import { EGroupTabs, Routes } from "../../types/NavigationPaths";
import { ErrorDisplay } from "../users/AddNewUser/ErrorDisplay";
import {
  CreateUserGroupFormData,
  createUserGroupSchema,
  DESCRIPTION_MAX_LENGTH,
  GROUP_NAME_MAX_LENGTH,
} from "./CreateUserGroupSchema";

interface AddUserGroupDialogProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
}

export const AddUserGroupDialog: React.FC<AddUserGroupDialogProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const userManagementClient = useUserManagementServiceV1();
  const navigate = useNavigate();

  const form = useForm<CreateUserGroupFormData>({
    resolver: zodResolver(createUserGroupSchema),
    defaultValues: {
      groupName: "",
      description: "",
      isDefaultUserGroup: false
    },
  });

  const { register, handleSubmit, reset, watch, formState, setError } = form;
  const { errors } = formState;

  const groupName = watch("groupName");
  const description = watch("description");

  const isMobile = useIsMobile();

  const actionText = useMemo(
    () =>
      isMobile
        ? t("groups.createGroupDialog.create")
        : t("groups.createGroupDialog.createGroup"),
    [isMobile, t],
  );

  const createGroupMutation = useMutation({
    mutationFn: (data: CreateUserGroupFormData) => {
      if (!companyId) {
        return Promise.reject(
          new Error("Company ID is required to create a user group."),
        );
      }

      return userManagementClient.createUserGroup(
        new UserManagement_V1.UserGroupWithoutId({
          userGroupName: data.groupName,
          description: data.description,
          companyId,
          isDefaultUserGroup: data.isDefaultUserGroup
        }),
      );
    },
    onSuccess: (createdGroup) => {
      void queryClient.invalidateQueries({ queryKey: [USER_GROUPS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [COMPANY_DEFAULT_USER_GROUPS_QUERY_KEY] });
      handleClose();
      void navigate(
        `/companies/${companyId}${Routes.GROUPS}/${createdGroup.userGroupId}?tab=${EGroupTabs.USERS}`,
      );
    },
    onError: (error) => {
      const castedError = error as Error & { status?: number };

      if (castedError.status === 409) {
        setError("groupName", {
          type: "manual",
          message: t("groups.createGroupDialog.groupNameInUse"),
        });
      }
    },
  });

  const errorMessage = useMemo(() => {
    if (createGroupMutation.error) {
      const castedError = createGroupMutation.error as Error & {
        status?: number;
      };

      if (castedError.status === 409) {
        return undefined;
      }

      return t("groups.createGroupDialog.errorMessage");
    }
  }, [createGroupMutation.error, t]);

  const onSubmit = (data: CreateUserGroupFormData) => {
    void createGroupMutation.mutateAsync(data);
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void handleSubmit(onSubmit)();
  };

  const handleClose = () => {
    reset();
    createGroupMutation.reset();
    onClose();
  };

  const handleNoNewLine = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const isSaveDisabled = !groupName.trim() || createGroupMutation.isPending;

  return (
    <ConfirmationDialog.Root
      aria-describedby="add-group-dialog-description"
      aria-labelledby="add-group-dialog-title"
      fullScreenOnMobile={true}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
    >
      <ConfirmationDialog.Title id="add-group-dialog-title" showIcon={false}>
        {t("groups.createGroupDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content sx={{ pb: 0 }}>
        <LabeledTextField
          {...register("groupName")}
          InputProps={{
            endAdornment: (
              <TextFieldCounter
                maxLength={GROUP_NAME_MAX_LENGTH}
                value={groupName}
              />
            ),
          }}
          error={!!errors.groupName}
          helperText={errors.groupName?.message}
          label={t("groups.createGroupDialog.groupName")}
          maxLength={GROUP_NAME_MAX_LENGTH}
          placeholder={t("groups.createGroupDialog.groupNamePlaceholder")}
          required
        />
        <LabeledTextField
          {...register("description")}
          InputProps={{
            endAdornment: (
              <TextFieldCounter
                maxLength={DESCRIPTION_MAX_LENGTH}
                multiline
                value={description ?? ""}
              />
            ),
          }}
          error={!!errors.description}
          height="auto"
          helperText={errors.description?.message}
          label={t("groups.createGroupDialog.description")}
          maxLength={DESCRIPTION_MAX_LENGTH}
          multiline
          onKeyDown={handleNoNewLine}
          placeholder={t("groups.createGroupDialog.descriptionPlaceholder")}
          rows={2}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: "flex", mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                {...register("isDefaultUserGroup")}
                checked={!!watch("isDefaultUserGroup")}
                color="primary"
              // react-hook-form register handles onChange
              />
            }
            label={t("groups.createGroupDialog.isDefaultUserGroup")}
            labelPlacement="end"
            sx={{ mb: 2 }}
          />
        </Box>
        {errorMessage && (
          <ErrorDisplay
            bottom={64}
            error={createGroupMutation.error}
            title={errorMessage}
          />
        )}
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          onClick={handleClose}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("groups.createGroupDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={isSaveDisabled}
          form="add-user-group-form"
          isLoading={createGroupMutation.isPending}
          loadingText={t("groups.createGroupDialog.creating")}
          onClick={handleFormSubmit}
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root >
  );
};
